import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { useCart } from "@/contexts/CartContext";
import api, { extractError } from "@/api/client";
import { getCurrentPositionOnce } from "@/hooks/useGeoTracking";
import { Truck, PackageCheck, MapPin, Banknote, Check, LocateFixed, CreditCard, Info, Wallet as WalletIcon, Plus } from "lucide-react";
import WalletTopupModal from "@/components/WalletTopupModal";

// Default destination: center of Yefren (Nafusa Mountains, Libya)
const DEFAULT_CITY = "يفرن";
const DEFAULT_LAT = 32.0665;
const DEFAULT_LNG = 12.5303;

export default function Checkout() {
  const cart = useCart();
  const nav = useNavigate();
  const [addr, setAddr] = useState({ label: "منزل", street: "", city: DEFAULT_CITY, lat: DEFAULT_LAT, lng: DEFAULT_LNG, notes: "" });
  const [customerNote, setCustomerNote] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [gpsOk, setGpsOk] = useState(false);
  const [accuracy, setAccuracy] = useState(null); // meters
  const [payMethod, setPayMethod] = useState("cash"); // cash | wallet | mypay
  const [walletBalance, setWalletBalance] = useState(0);
  const [topupOpen, setTopupOpen] = useState(false);
  const [mypayStatus, setMypayStatus] = useState({ enabled: false, fee_percent: 0 });

  const refreshWallet = async () => {
    try {
      const { data } = await api.get("/wallet/me");
      setWalletBalance(data.balance || 0);
    } catch { /* ignore */ }
  };

  // Poll MyPay status once so we can hide/show the option without leaking config.
  const refreshMypayStatus = async () => {
    try {
      const { data } = await api.get("/payments/mypay/status");
      const orderEnabled = !!(data.enabled && data.order_payment_enabled);
      setMypayStatus({ ...data, enabled: orderEnabled });
    } catch { /* ignore */ }
  };

  useEffect(() => {
    refreshWallet();
    refreshMypayStatus();
  }, []);

  // Sync destination coordinates into cart context so delivery_fee stays live
  useEffect(() => {
    if (cart.fulfillment === "delivery") {
      cart.setDestination({ lat: addr.lat, lng: addr.lng });
    } else {
      cart.setDestination(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addr.lat, addr.lng, cart.fulfillment]);

  const requestGpsLocation = async ({ silent = false } = {}) => {
    setGpsBusy(true); if (!silent) setErr("");
    try {
      const c = await getCurrentPositionOnce();
      setAddr((a) => ({ ...a, lat: c.lat, lng: c.lng }));
      setAccuracy(c.accuracy || null);
      setGpsOk(true);
    } catch (e) {
      if (!silent) setErr(e.message || "تعذّر تحديد موقعك");
      setGpsOk(false);
    } finally {
      setGpsBusy(false);
    }
  };

  // Auto-request GPS on first mount for delivery orders (silent — no error if denied,
  // user can still enter address manually). Precise-lock via getCurrentPositionOnce.
  useEffect(() => {
    if (cart.fulfillment === "delivery" && !gpsOk) {
      requestGpsLocation({ silent: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pickup orders are cash-only (paid at the shop). If the customer had picked
  // wallet or MyPay and later switched to pickup, snap them back to cash.
  useEffect(() => {
    if (cart.fulfillment === "pickup" && payMethod !== "cash") {
      setPayMethod("cash");
    }
  }, [cart.fulfillment, payMethod]);

  const accuracyLabel = accuracy == null
    ? null
    : accuracy <= 15 ? { text: "دقيق جداً", color: "text-green-700 bg-green-100" }
    : accuracy <= 50 ? { text: "دقيق", color: "text-blue-700 bg-blue-100" }
    : { text: "تقريبي", color: "text-yellow-700 bg-yellow-100" };

  const submit = async () => {
    if (cart.items.length === 0) return;
    if (!cart.restaurant) { setErr("يرجى اختيار مطعم أولاً"); return; }
    if (cart.fulfillment === "delivery" && !addr.street) {
      setErr("الرجاء إدخال عنوان التوصيل"); return;
    }
    setBusy(true); setErr("");
    try {
      const payload = {
        restaurant_id: cart.restaurant.id,
        items: cart.items.map((it) => ({
          menu_item_id: it.menu_item_id, name: it.name,
          unit_price: it.unit_price, quantity: it.quantity,
          extras: it.extras, notes: it.notes, line_total: it.line_total
        })),
        subtotal: cart.subtotal,
        delivery_fee: cart.delivery_fee,
        total: cart.total,
        fulfillment: cart.fulfillment,
        address: cart.fulfillment === "delivery" ? addr : null,
        payment_method: payMethod,
        customer_note: customerNote,
      };
      const { data } = await api.post("/orders", payload);
      // MyPay direct payment: the order is created in `pending_payment` and
      // won't be visible to the restaurant until MyPay confirms via webhook.
      // We redirect the customer to MyPay's hosted card-entry page; the
      // return_url points to /wallet/mypay/order-return which polls and then
      // routes to /track/<order_id> on success.
      if (payMethod === "mypay" && data?.mypay_payment_url) {
        cart.clear();
        window.location.href = data.mypay_payment_url;
        return;
      }
      cart.clear();
      nav(`/track/${data.id}`);
    } catch (e) {
      setErr(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--background)]">
      <TopBar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <h1 className="text-3xl font-black mb-6" style={{fontFamily:'Cairo'}}>إتمام الطلب</h1>

        {/* Restaurant snapshot */}
        {cart.restaurant && (
          <div className="bg-white rounded-2xl p-5 talabya-shadow-soft mb-4" data-testid="co-restaurant">
            <div className="text-sm font-bold mb-2">المطعم</div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-light text-primary-brand flex items-center justify-center">
                <MapPin size={16}/>
              </div>
              <div className="flex-1">
                <div className="font-bold">{cart.restaurant.name}</div>
                <div className="text-xs text-[var(--text-secondary)]">{cart.restaurant.address}</div>
              </div>
            </div>
          </div>
        )}

        {/* Fulfillment */}
        <div className="bg-white rounded-2xl p-5 talabya-shadow-soft mb-4">
          <div className="text-sm font-bold mb-3">طريقة الاستلام</div>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => cart.setFulfillment("delivery")}
              className={`p-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition ${cart.fulfillment === "delivery" ? "border-primary-brand bg-primary-light" : "border-[var(--border)]"}`}
              data-testid="co-delivery">
              <Truck className={cart.fulfillment === "delivery" ? "text-primary-brand" : ""} />
              <div className="font-bold">توصيل للعنوان</div>
              <div className="text-xs text-[var(--text-secondary)]">30-45 دقيقة</div>
            </button>
            <button onClick={() => { cart.setFulfillment("pickup"); setPayMethod("cash"); }}
              className={`p-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition ${cart.fulfillment === "pickup" ? "border-primary-brand bg-primary-light" : "border-[var(--border)]"}`}
              data-testid="co-pickup">
              <PackageCheck className={cart.fulfillment === "pickup" ? "text-primary-brand" : ""} />
              <div className="font-bold">استلام من المحل</div>
              <div className="text-xs text-[var(--text-secondary)]">جاهز خلال 15 دقيقة</div>
            </button>
          </div>
        </div>

        {/* Address */}
        {cart.fulfillment === "delivery" && (
          <div className="bg-white rounded-2xl p-5 talabya-shadow-soft mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MapPin size={18} className="text-primary-brand"/>
                <div className="text-sm font-bold">عنوان التوصيل</div>
              </div>
              <button
                type="button"
                onClick={() => requestGpsLocation()}
                disabled={gpsBusy}
                className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg transition ${gpsOk ? "bg-green-100 text-green-700" : "bg-primary-light text-primary-brand hover:bg-primary-brand hover:text-white"} disabled:opacity-60`}
                data-testid="use-my-location">
                {gpsOk ? <><Check size={14}/> تم تحديد موقعك</> : <><LocateFixed size={14} className={gpsBusy ? "animate-pulse-dot" : ""}/> {gpsBusy ? "جاري التحديد..." : "استخدم موقعي الحالي"}</>}
              </button>
            </div>
            {accuracyLabel && (
              <div className="flex items-center gap-2 text-[11px] mb-3">
                <span className={`px-2 py-0.5 rounded-full font-bold ${accuracyLabel.color}`} data-testid="gps-accuracy-badge">
                  {accuracyLabel.text}
                </span>
                <span className="text-[var(--text-secondary)]" data-testid="gps-accuracy-value">
                  دقة ±{Math.round(accuracy)} متر · إحداثيات: {addr.lat.toFixed(6)}, {addr.lng.toFixed(6)}
                </span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="التسمية (منزل، عمل)" value={addr.label} onChange={(e) => setAddr({ ...addr, label: e.target.value })}
                className="px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right" data-testid="addr-label"/>
              <input placeholder="المدينة" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })}
                className="px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none text-right" data-testid="addr-city"/>
              <input placeholder="الشارع/المنطقة" value={addr.street} onChange={(e) => setAddr({ ...addr, street: e.target.value })}
                className="px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none col-span-2 text-right" data-testid="addr-street"/>
              <textarea rows={2} placeholder="ملاحظات على العنوان (طابق، علامة مميزة...)"
                value={addr.notes} onChange={(e) => setAddr({ ...addr, notes: e.target.value })}
                className="px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none col-span-2 text-right resize-none" data-testid="addr-notes"/>
            </div>
          </div>
        )}

        {/* Note */}
        <div className="bg-white rounded-2xl p-5 talabya-shadow-soft mb-4">
          <div className="text-sm font-bold mb-2">ملاحظة للمطعم (اختياري)</div>
          <textarea rows={2} value={customerNote} onChange={(e) => setCustomerNote(e.target.value)}
            placeholder="نكهات محددة، وقت التسليم..."
            className="w-full px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none resize-none text-right"
            data-testid="checkout-note"/>
        </div>

        {/* Payment */}
        <div className="bg-white rounded-2xl p-5 talabya-shadow-soft mb-4">
          <div className="text-sm font-bold mb-3">طريقة الدفع</div>
          {cart.fulfillment === "pickup" ? (
            <>
              <div
                className="flex items-center justify-between p-4 rounded-2xl border-2 border-primary-brand bg-primary-light"
                data-testid="pay-cash-shop"
              >
                <div className="flex items-center gap-3">
                  <Banknote className="text-primary-brand"/>
                  <div>
                    <div className="font-bold">الدفع نقداً في المحل</div>
                    <div className="text-xs text-[var(--text-secondary)]">تدفع مباشرة للمحل عند الاستلام</div>
                  </div>
                </div>
                <Check className="text-primary-brand"/>
              </div>
              <div
                className="mt-2 text-[11px] leading-relaxed text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5"
                data-testid="pickup-cash-notice"
              >
                ⚠️ الدفع يتم <b>نقداً داخل المحل مباشرة</b>. تطبيق طلبية غير مسؤول عن استلام المبلغ في طلبات الاستلام من المحل.
              </div>
            </>
          ) : (
            <>
              <label
                onClick={() => setPayMethod("cash")}
                className={`flex items-center justify-between p-4 rounded-2xl border-2 cursor-pointer mb-2 transition ${payMethod === "cash" ? "border-primary-brand bg-primary-light" : "border-[var(--border)] bg-white hover:bg-[var(--surface-alt)]"}`}
                data-testid="pay-cash">
                <div className="flex items-center gap-3">
                  <Banknote className={payMethod === "cash" ? "text-primary-brand" : "text-[var(--text-secondary)]"}/>
                  <div>
                    <div className="font-bold">الدفع نقداً عند الاستلام</div>
                    <div className="text-xs text-[var(--text-secondary)]">ادفع للسائق عند وصول طلبك</div>
                  </div>
                </div>
                {payMethod === "cash" && <Check className="text-primary-brand"/>}
              </label>
          <label
            onClick={() => walletBalance >= (cart.total || 0) && setPayMethod("wallet")}
            className={`flex items-center justify-between gap-2 p-4 rounded-2xl border-2 mb-2 transition ${payMethod === "wallet" ? "border-primary-brand bg-primary-light cursor-pointer" : walletBalance < (cart.total || 0) ? "border-[var(--border)] bg-[var(--surface-alt)]" : "border-[var(--border)] bg-white hover:bg-[var(--surface-alt)] cursor-pointer"}`}
            data-testid="pay-wallet">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <WalletIcon className={`shrink-0 ${payMethod === "wallet" ? "text-primary-brand" : "text-[var(--text-secondary)]"}`}/>
              <div className="min-w-0">
                <div className="font-bold">الدفع من المحفظة</div>
                <div className="text-xs text-[var(--text-secondary)]">
                  الرصيد: <span className="font-bold text-primary-brand">{walletBalance.toFixed(2)} د.ل</span>
                  {walletBalance < (cart.total || 0) && <span className="text-red-600"> — غير كافٍ</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTopupOpen(true); }}
                className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-primary-brand text-white text-xs font-black hover:opacity-90 shadow-sm"
                data-testid="wallet-topup-btn"
              >
                <Plus size={14}/> شحن
              </button>
              {payMethod === "wallet" && <Check className="text-primary-brand"/>}
            </div>
          </label>
          {mypayStatus.enabled && (
            <label
              onClick={() => setPayMethod("mypay")}
              className={`flex items-center justify-between gap-2 p-4 rounded-2xl border-2 cursor-pointer mb-2 transition ${payMethod === "mypay" ? "border-primary-brand bg-primary-light" : "border-[var(--border)] bg-white hover:bg-[var(--surface-alt)]"}`}
              data-testid="pay-mypay">
              <div className="flex items-center gap-3 min-w-0">
                <CreditCard className={`shrink-0 ${payMethod === "mypay" ? "text-primary-brand" : "text-[var(--text-secondary)]"}`}/>
                <div className="min-w-0">
                  <div className="font-bold">الدفع الإلكتروني (MyPay)</div>
                  <div className="text-xs text-[var(--text-secondary)]">
                    يسر · مصرفي · معاملات · إدفعلي · صحارى
                    {mypayStatus.fee_percent > 0 && (
                      <> · <span className="font-bold">رسوم {mypayStatus.fee_percent}%</span> = <span className="font-black">{((cart.total || 0) * (1 + mypayStatus.fee_percent / 100)).toFixed(2)} د.ل</span></>
                    )}
                  </div>
                </div>
              </div>
              {payMethod === "mypay" && <Check className="text-primary-brand"/>}
            </label>
          )}
          <div className="text-[11px] text-[var(--text-secondary)] bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 mt-1">
            💡 الدفع نقداً عند الاستلام، أو من محفظتك (رصيد كروت شحن){mypayStatus.enabled ? "، أو مباشرة عبر MyPay (بطاقة أو بوابات ليبية)." : "."}
          </div>
            </>
          )}
        </div>

        {/* Summary */}
        <div className="bg-white rounded-2xl p-5 talabya-shadow-soft">
          <div className="text-sm font-bold mb-3">ملخص الطلب ({cart.items.length} صنف)</div>
          {cart.items.map((it, i) => (
            <div key={i} className="flex justify-between text-sm py-1 text-[var(--text-secondary)]">
              <span>{it.name} × {it.quantity}</span>
              <span>{it.line_total.toFixed(2)} د.ل</span>
            </div>
          ))}
          <div className="border-t border-[var(--border)] my-2"/>
          <div className="flex justify-between py-1"><span>المجموع الفرعي</span><span>{cart.subtotal.toFixed(2)} د.ل</span></div>
          <div className="flex justify-between py-1">
            <span className="flex items-center gap-1">
              رسوم التوصيل
              {cart.fulfillment === "delivery" && cart.distance_km != null && (
                <span className="text-[10px] text-[var(--text-secondary)] bg-[var(--surface-alt)] px-1.5 py-0.5 rounded" data-testid="distance-info">
                  {cart.distance_km.toFixed(1)} كم
                </span>
              )}
            </span>
            <span data-testid="checkout-delivery-fee">{cart.delivery_fee.toFixed(2)} د.ل</span>
          </div>
          {cart.fulfillment === "delivery" && (
            <div className="text-[10px] text-[var(--text-secondary)] flex items-start gap-1 mt-0.5 pr-1" data-testid="pricing-policy">
              <Info size={10} className="mt-0.5 shrink-0"/>
              <span>سعر التوصيل: {cart.pricing.base_fee} د.ل حتى {cart.pricing.base_km} كم، ثم {cart.pricing.per_km} د.ل لكل كيلومتر إضافي</span>
            </div>
          )}
          <div className="flex justify-between mt-2 pt-2 border-t border-[var(--border)]"><span className="font-bold">الإجمالي</span><span className="font-black text-xl text-primary-brand" style={{fontFamily:'Cairo'}}>{cart.total.toFixed(2)} د.ل</span></div>

          {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mt-3" data-testid="checkout-error">{err}</div>}

          <button onClick={submit} disabled={busy || cart.items.length === 0}
            className="w-full mt-4 py-4 rounded-2xl bg-primary-brand text-white font-bold text-base talabya-shadow-float hover:opacity-95 disabled:opacity-60"
            data-testid="place-order">
            {busy ? "جاري إنشاء الطلب..." : `تأكيد الطلب • ${cart.total.toFixed(2)} د.ل`}
          </button>
        </div>
      </div>

      <WalletTopupModal
        open={topupOpen}
        onClose={() => setTopupOpen(false)}
        onSuccess={(newBalance) => {
          if (typeof newBalance === "number") setWalletBalance(newBalance);
          else refreshWallet();
        }}
      />
    </div>
  );
}
